import { type RefObject, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import type { ChartData, Chart as ChartJS, ChartOptions, Plugin } from 'chart.js';

import './registerCharts';

import { useChartAccessibleName } from './chartAccessibleName';
import { createDoubleClickResetPlugin } from './plugins/doubleClickResetPlugin';
import { createNoDataPlugin } from './plugins/noDataPlugin';
import { useChartTheme } from './theme/ChartThemeContext';
import { useChartUpdateMode } from './theme/useChartUpdateMode';
import { withNoDataColor } from './withNoDataColor';

/**
 * Every chart gets the no-data message by default, ahead of the caller's plugins. A circular
 * chart whose slices are all zero has nothing to draw either, so it counts as empty too.
 */
const NO_DATA = {
    line: createNoDataPlugin<'line'>(),
    bar: createNoDataPlugin<'bar'>(),
    pie: createNoDataPlugin<'pie'>({ zeroIsEmpty: true }),
    doughnut: createNoDataPlugin<'doughnut'>({ zeroIsEmpty: true }),
};

/** Axis charts reset their zoom on a double click inside the plot. */
const DOUBLE_CLICK_RESET = {
    line: createDoubleClickResetPlugin<'line'>(),
    bar: createDoubleClickResetPlugin<'bar'>(),
};

interface TypedChartProps<TType extends 'line' | 'bar' | 'pie' | 'doughnut'> {
    type: TType;
    data: ChartData<TType>;
    options?: ChartOptions<TType>;
    plugins?: Plugin<TType>[];
    chartRef?: RefObject<ChartJS<TType> | null>;
}

interface SharedProps {
    /**
     * Axis charts only. True keeps a 2:1 plot; false fills the sized element the chart sits in.
     * Circular charts size themselves through their own options.
     */
    maintainAspectRatio?: boolean;
    /**
     * Already translated. Names the canvas for screen readers, with the series (or, on a
     * circular chart, the slices); the title of the card the chart sits in when left out.
     */
    title?: string;
}

export type BaseChartProps = SharedProps &
    (
        | TypedChartProps<'line'>
        | TypedChartProps<'bar'>
        | TypedChartProps<'pie'>
        | TypedChartProps<'doughnut'>
    );

export type BaseChartType = BaseChartProps['type'];

/**
 * The one Chart.js wrapper every chart in the app draws through: it registers Chart.js,
 * follows the applied theme (no-data colour, no animation on a theme switch), names the
 * canvas for screen readers, draws the no-data message and, on line and bar charts, resets
 * the zoom on a double click. MetricChart and CircularChart are
 * its only callers; a page renders one of those, never this.
 */
export default function BaseChart(props: BaseChartProps) {
    const { maintainAspectRatio = true, title } = props;
    const circular = props.type === 'pie' || props.type === 'doughnut';

    const theme = useChartTheme();
    const updateMode = useChartUpdateMode(theme, props.data, props.options);
    const accessibleName = useChartAccessibleName(
        title,
        props.data,
        circular ? 'slices' : 'series'
    );

    // The element is memoised on the props it reads, so a parent re-render that changes none
    // of them does not reach Chart.js.
    const chart = useMemo(() => {
        const shared = { 'aria-label': accessibleName, updateMode };
        switch (props.type) {
            case 'line':
                return (
                    <Chart
                        type="line"
                        data={props.data}
                        options={{
                            ...props.options,
                            plugins: withNoDataColor(props.options?.plugins, theme.text),
                            maintainAspectRatio,
                        }}
                        plugins={[NO_DATA.line, DOUBLE_CLICK_RESET.line, ...(props.plugins ?? [])]}
                        ref={props.chartRef}
                        {...shared}
                    />
                );
            case 'bar':
                return (
                    <Chart
                        type="bar"
                        data={props.data}
                        options={{
                            ...props.options,
                            plugins: withNoDataColor(props.options?.plugins, theme.text),
                            maintainAspectRatio,
                        }}
                        plugins={[NO_DATA.bar, DOUBLE_CLICK_RESET.bar, ...(props.plugins ?? [])]}
                        ref={props.chartRef}
                        {...shared}
                    />
                );
            case 'pie':
                return (
                    <Chart
                        type="pie"
                        data={props.data}
                        options={{
                            ...props.options,
                            plugins: withNoDataColor(props.options?.plugins, theme.text),
                        }}
                        plugins={[NO_DATA.pie, ...(props.plugins ?? [])]}
                        ref={props.chartRef}
                        {...shared}
                    />
                );
            case 'doughnut':
                return (
                    <Chart
                        type="doughnut"
                        data={props.data}
                        options={{
                            ...props.options,
                            plugins: withNoDataColor(props.options?.plugins, theme.text),
                        }}
                        plugins={[NO_DATA.doughnut, ...(props.plugins ?? [])]}
                        ref={props.chartRef}
                        {...shared}
                    />
                );
        }
    }, [
        props.type,
        props.data,
        props.options,
        props.plugins,
        props.chartRef,
        maintainAspectRatio,
        theme,
        accessibleName,
        updateMode,
    ]);

    if (circular) return chart;

    // An axis chart sits in a box of its own, so Chart.js sizes the canvas to it: 2:1 when it
    // keeps its aspect ratio, otherwise the full size of the element the caller sized.
    const containerStyle = maintainAspectRatio
        ? { aspectRatio: '2' }
        : { width: '100%', height: '100%' };
    return <div style={containerStyle}>{chart}</div>;
}
